import React, { useState, useEffect, useRef, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import './calendar.css';
import timeGridPlugin from "@fullcalendar/timegrid";
import timelinePlugin from "@fullcalendar/timeline";
import rrulePlugin from "@fullcalendar/rrule";
import { 
  Box, Dialog, Button, Typography, Chip,
  Select, MenuItem, IconButton, CircularProgress, useTheme
} from "@mui/material";
import { Refresh, Close } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import ApiService from "../../API/Requesthandler";

const ModernDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: "16px",
    boxShadow: theme.shadows[10]
  }
}));

const TaskCalendar = () => {
  const theme = useTheme();
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [open, setOpen] = useState(false);
  const [currentView, setCurrentView] = useState("dayGridWeek");
  const [data, setData] = useState([]);
  const [eventFilter, setEventFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  // Multiple colors for each event type
  const eventColors = useMemo(() => ({
    minutely: [
      theme.palette.warning.main,
      theme.palette.warning.dark,
      theme.palette.warning.light
    ],
    hourly: [
      theme.palette.info.main,
      theme.palette.info.dark,
      theme.palette.info.light
    ],
    daily: [
      theme.palette.success.main,
      theme.palette.success.dark,
      theme.palette.success.light
    ],
    weekly: [
      theme.palette.secondary.main,
      theme.palette.secondary.dark,
      theme.palette.secondary.light
    ],
    monthly: [
      theme.palette.primary.main,
      theme.palette.primary.dark,
      theme.palette.primary.light
    ],
    yearly: [
      theme.palette.error.main,
      theme.palette.error.dark,
      theme.palette.error.light
    ],
    postpone: [
      theme.palette.text.secondary,
      theme.palette.text.disabled,
      theme.palette.text.primary
    ]
  }), [theme.palette]);

  // Helper function to get a random color from the array
  const getRandomColor = (eventType) => {
    const colors = eventColors[eventType] || [theme.palette.text.secondary];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const getScheduleCampaigns = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user-data"));
      if (!userData?.id) return;
      
      const response = await ApiService.get("/api/chat/get_postpond_schedule_messages/"+userData.id );
      
      setData(response.data);
      
    } catch (err) {
      console.error("Failed to load scheduled messages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getScheduleCampaigns();
  }, []);


  useEffect(() => {
    console.log(data)
  }, [data]);

  const handleViewChange = (newView) => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(newView);
    }
    setCurrentView(newView);
  };

  const handleDatesSet = (arg) => {
    setCurrentView(arg.view.type);
  };

const parseDayFilters = (dayFilter) => {
  const dayMap = {
    // Corrected ISO weekday mapping (Monday=0 to Sunday=6)
    sun: 6, sunday: 6,    // Sunday = 6 (RRULE: SU)
    mon: 0, monday: 0,    // Monday = 0 (RRULE: MO)
    tue: 1, tuesday: 1,   // Tuesday = 1 (RRULE: TU)
    wed: 2, wednesday: 2, // Wednesday = 2 (RRULE: WE)
    thu: 3, thursday: 3,  // Thursday = 3 (RRULE: TH)
    fri: 4, friday: 4,    // Friday = 4 (RRULE: FR)
    sat: 5, saturday: 5   // Saturday = 5 (RRULE: SA)
  };

  if (!dayFilter || dayFilter.length === 0) {
    return [0, 1, 2, 3, 4, 5, 6]; // Monday to Sunday order
  }

  return dayFilter
    .map(day => dayMap[day.toLowerCase().trim()])
    .filter(val => val !== undefined);
};
  
  const parsedEvents = useMemo(() => {
    return data.flatMap((task) => {
      try {
        const filters = JSON.parse(task.filters);
        const repeat = filters?.repeat;
        const taskDateTime = new Date(filters.timeSlot)
        taskDateTime.setHours(taskDateTime.getHours() - 7)
          console.log('Local:', taskDateTime.toString());
          console.log('UTC:', taskDateTime.toUTCString());
        if (isNaN(taskDateTime.getTime())) {
          console.error("Invalid date_time for task:", task);
          return [];
        }
  
        // Handle POSTPOND events: create one event per date in daterange
        if (task.type === "POSTPOND") {
          const daterange = filters.daterange || [];
          
          // Extract time components from original date_time
          const timeHours = taskDateTime.getUTCHours();
          const timeMinutes = taskDateTime.getUTCMinutes();
          const timeSeconds = taskDateTime.getUTCSeconds();
  
          return daterange
            .map((dateStr, index) => {
              const trimmedDateStr = dateStr.trim();
              const datePart = new Date(trimmedDateStr + 'T00:00:00Z'); // Parse as UTC date
              
              if (isNaN(datePart.getTime())) {
                console.error("Invalid date in daterange:", dateStr, "for task:", task);
                return null;
              }
  
              // Create UTC datetime combining date from daterange and time from date_time
              const eventDate = new Date(Date.UTC(
                datePart.getUTCFullYear(),
                datePart.getUTCMonth(),
                datePart.getUTCDate(),
                timeHours,
                timeMinutes,
                timeSeconds
              ));
  
              return {
                id: `${task.id}_${index}`,
                title: task.message_header,
                extendedProps: {
                  ...task,
                  filters,
                  eventType: "postpone",
                },
                color: getRandomColor("postpone"),
                start: eventDate.toISOString(),
              };
            })
            .filter(Boolean);
        }
  
        // Handle non-POSTPOND events
        const baseEvent = {
          id: task.id,
          title: task.message_header,
          extendedProps: {
            ...task,
            filters,
            eventType: "postpone",
          },
          color: getRandomColor("postpone"),
          start: taskDateTime.toISOString(), // Use date_time directly
        };
  
        // Existing logic for non-POSTPOND tasks (SCHEDULE, etc.)
        const startDate = new Date(filters.start);
        // const baseEvent = {
        //   id: task.id,
        //   title: task.message_header,
        //   extendedProps: {
        //     ...task,
        //     filters,
        //     eventType: "postpone",
        //   },
        //   color: getRandomColor("postpone"),
        //   start: startDate.toISOString(),
        // };
  
        if (repeat && repeat.unit && repeat.value) {
          const frequencyMap = {
            minute: "MINUTELY",
            minutes: "MINUTELY",
            hour: "HOURLY",
            hours: "HOURLY",
            day: "DAILY",
            days: "DAILY",
            week: "WEEKLY",
            weeks: "WEEKLY",
            month: "MONTHLY",
            months: "MONTHLY",
            year: "YEARLY",
            years: "YEARLY",
          };
  
          const normalizedUnit = repeat.unit.toLowerCase().replace(/s$/, "");
          const freq = frequencyMap[normalizedUnit] || "DAILY";
          const interval = Math.max(1, parseInt(repeat.value) || 1);
  
          const rruleConfig = {
            freq,
            interval,
            dtstart: taskDateTime.toISOString(), // Use date_time instead of filters.start
            until: filters.end ? new Date(filters.end).toISOString() : undefined
          };
  
          let weekdays = parseDayFilters(filters.dayFilter)
            console.log("Initial weekdays:", weekdays);

            if (freq === "WEEKLY") {
              const isAutoWeekly =
                (!filters.dayFilter || filters.dayFilter.length === 0) &&
                repeat.unit.toLowerCase() === "weeks" &&
                repeat.value === 1;

              if (isAutoWeekly) {
                const jsDay = startDate.getDay();
                console.log("JS DAY>>>>>")
                console.log(jsDay)
                // Convert JavaScript day to RRULE day (Sunday=6, Monday=0, etc.)
                const rruleDay = jsDay === 0 ? 6 : jsDay - 1;
                console.log(`Auto-weekly: JS day ${jsDay} → RRULE day ${rruleDay}`);
                weekdays = [rruleDay] ;
              }
            }

            console.log("Final weekdays:", weekdays);
  
          if (freq === "MONTHLY") {
            const isAutoMonthly =
              (!filters.dayFilter || filters.dayFilter.length === 0) &&
              repeat.unit.toLowerCase() === "months";
            if (isAutoMonthly) {
              weekdays = [parseInt(filters.monthdate)];
            }
          }
  
          if (freq === "MONTHLY") {
            if (weekdays.length > 0) {
              rruleConfig.bymonthday = weekdays;
            }
          } else {
            if (weekdays.length > 0) {
              rruleConfig.byweekday = weekdays;
            }
          }
  
          rruleConfig.interval = interval;
  
          if (freq === "MINUTELY") {
            rruleConfig.count = 100;
          }
  
          const eventTypeMap = {
            MINUTELY: "minutely",
            HOURLY: "hourly",
            DAILY: "daily",
            WEEKLY: "weekly",
            MONTHLY: "monthly",
            YEARLY: "yearly",
          };
  
          const eventType = eventTypeMap[freq] || "postpone";
  
          return [
            {
              ...baseEvent,
              rrule: rruleConfig,
              color: getRandomColor(eventType),
              extendedProps: {
                ...baseEvent.extendedProps,
                eventType,
              },
            },
          ];
        }
  
        return [baseEvent];
      } catch (error) {
        console.error("Error parsing event:", error);
        return [];
      }
    });
  }, [data, eventColors]);

  useEffect(() => {
    setEvents(parsedEvents);
  }, [parsedEvents]);

  useEffect(() => {
    setFilteredEvents(
      eventFilter === 'all' 
        ? events 
        : events.filter(event => event.extendedProps.eventType === eventFilter)
    );
  }, [eventFilter, events]);

  const handleEventClick = (info) => {
    setSelectedEvent(info.event);
    setOpen(true);
  };

  const renderEventContent = (eventInfo) => (
    <Box sx={{ p: 0.5 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{
          width: 8,
          height: 8,
          backgroundColor: eventInfo.event.backgroundColor,
          borderRadius: '50%',
          marginRight: 8
        }} />
        <Typography variant="caption">
          {eventInfo.event.title ? eventInfo.event.title : "Untitled Event"}
        </Typography>
      </div>
    </Box>
  );

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.default,
      minHeight: "70vh",
      color: theme.palette.text.primary,
      paddingLeft: "10px",
      paddingRight: "10px"
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h4" sx={{ fontWeight: 500 }}>
          Schedule Calendar
          <IconButton onClick={getScheduleCampaigns} sx={{ ml: 1 }}>
            <Refresh sx={{ color: theme.palette.primary.main }} />
          </IconButton>
        </Typography>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Select
            value={currentView}
            onChange={(e) => handleViewChange(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ 
              minWidth: 150,
              backgroundColor: theme.palette.background.paper,
              '& .MuiSelect-select': { py: 1 }
            }}
          >
            <MenuItem value="dayGridMonth">Month</MenuItem>
            <MenuItem value="timeGridWeek">Week</MenuItem>
            <MenuItem value="timeGridDay">Day</MenuItem>
            <MenuItem value="timelineDay">Timeline Day</MenuItem>
            <MenuItem value="timelineWeek">Timeline Week</MenuItem>
          </Select>

          <Select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ 
              minWidth: 150,
              backgroundColor: theme.palette.background.paper,
              '& .MuiSelect-select': { py: 1 }
            }}
          >
            <MenuItem value="all">All Events</MenuItem>
            {Object.keys(eventColors).map(type => (
              <MenuItem key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Box sx={{ 
        height: 'calc(100vh - 160px)',
        borderRadius: 4,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative'
      }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%'
          }}>
            <CircularProgress sx={{ color: theme.palette.primary.main }} />
          </Box>
        ) : (
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, timelinePlugin, rrulePlugin]}
            initialView={currentView}
            events={filteredEvents}
            eventClick={handleEventClick}
            editable={true}
            nowIndicator={true}
            datesSet={handleDatesSet}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: ''
            }}
            height="100%"
            eventContent={renderEventContent}
            timeZone="UTC"
          />
        )}
      </Box>

      <ModernDialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <Box sx={{ p: 3 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3
          }}>
            <Typography variant="h5">{selectedEvent?.title ? selectedEvent?.title : "Untitled Event"}</Typography>
            <IconButton onClick={() => setOpen(false)}>
              <Close sx={{ color: theme.palette.text.primary }} />
            </IconButton>
          </Box>

          <Box sx={{ 
            backgroundColor: theme.palette.background.default,
            borderRadius: 2,
            p: 2,
            mb: 3
          }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedEvent?.extendedProps.message_text}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={selectedEvent?.extendedProps.eventType}
                sx={{ 
                  backgroundColor: `${selectedEvent?.backgroundColor}30`,
                  color: selectedEvent?.backgroundColor
                }}
              />
              <Chip
                label={selectedEvent?.extendedProps.status}
                color={selectedEvent?.extendedProps.status === 'active' ? 'success' : 'error'}
              />
              <Chip
                label={new Date(selectedEvent?.extendedProps.date_time).toLocaleDateString()}
              />
              <Chip
                label={new Date(selectedEvent?.extendedProps.filters.timeSlot).toLocaleTimeString()}
              />
            </Box>

            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mb: 1 }}>
             
            </Typography>
            <pre style={{ 
              backgroundColor: theme.palette.background.paper,
              padding: "12px",
              borderRadius: "8px",
              fontSize: "0.85em",
              overflowX: "auto",
              margin: 0
            }}>
              {/* {JSON.stringify(selectedEvent?.extendedProps.filters, null, 2)} */}
            </pre>
          </Box>

          <Button 
            fullWidth 
            variant="contained"
            onClick={() => setOpen(false)}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              color: theme.palette.primary.contrastText,
              textTransform: 'none'
            }}
          >
            Close Details
          </Button>
        </Box>
      </ModernDialog>

      
    </Box>
  );
};

export default TaskCalendar;